import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { searchActivities } from '../../util/api';

interface SearchPageViewState {
  activities: any[];
  neighborhoods: string[];
  facetDistribution: Record<string, any> | null;
  usedMeilisearch: boolean | null;
  searchInProgress: boolean;
  searchError: string | null;
}

// ─── Async Thunk ───

const fetchSearchResultsThunk = createAsyncThunk(
  'SearchPageView/fetchSearchResults',
  async (params: Record<string, string>, { rejectWithValue }: any) => {
    try {
      const response = await searchActivities(params);
      return {
        activities: response.activities || [],
        neighborhoods: response.neighborhoods || [],
        facetDistribution: response.facetDistribution || {},
        usedMeilisearch: !!response.usedMeilisearch,
      };
    } catch (e: any) {
      console.error('SearchPageView: activity search failed', e);
      return rejectWithValue(e.message || 'Search failed');
    }
  }
);

// ─── Slice ───

const initialState: SearchPageViewState = {
  activities: [],
  neighborhoods: [],
  facetDistribution: null,
  usedMeilisearch: null,
  searchInProgress: false,
  searchError: null,
};

const slice = createSlice({
  name: 'SearchPageView',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSearchResultsThunk.pending, s => {
        s.searchInProgress = true;
        s.searchError = null;
      })
      .addCase(fetchSearchResultsThunk.fulfilled, (s, a) => {
        s.searchInProgress = false;
        s.activities = a.payload.activities;
        s.neighborhoods = a.payload.neighborhoods;
        s.facetDistribution = a.payload.facetDistribution;
        s.usedMeilisearch = a.payload.usedMeilisearch;
      })
      .addCase(fetchSearchResultsThunk.rejected, (s, a) => {
        s.searchInProgress = false;
        s.searchError = a.payload as string;
      });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchSearchResults = (params: Record<string, string>) => (dispatch: any) =>
  dispatch(fetchSearchResultsThunk(params)).unwrap();

// ─── Selectors ───

export const selectSearchActivities = (s: any) => s.SearchPageView?.activities || [];
export const selectSearchNeighborhoods = (s: any) => s.SearchPageView?.neighborhoods || [];
export const selectSearchInProgress = (s: any) => s.SearchPageView?.searchInProgress || false;
export const selectSearchError = (s: any) => s.SearchPageView?.searchError || null;
export const selectFacetDistribution = (s: any) => s.SearchPageView?.facetDistribution || null;
export const selectSearchUsedMeilisearch = (s: any) => s.SearchPageView?.usedMeilisearch ?? null;
